import React, {useState, useEffect, useMemo, useCallback} from 'react'
import _ from 'lodash'

// configuration
import useGetConfigurationReducer from '../../state/reservation/get-configuration/reducer'
import {getConfiguration} from '../../state/reservation/get-configuration/actions'

// entities
import {getStepEntities} from '../../state/entities/get-step-entities/actions'
import useGetStepEntitiesReducer from '../../state/entities/get-step-entities/reducer'

import {transformDateFormat, transformTimeFormat} from './util'

import {getEntity} from '../../state/entities/get-entity/actions'
import {useEntityReducer} from '../../state/entities/get-entity/reducer'

// items
import {getReservationEntityItems} from '../../state/entities/get-reservation-entity-items/actions'
import useGetReservationEntityItemsReducer, {
  ActionTypes as getReservationEntityItemsActionTypes,
} from '../../state/entities/get-reservation-entity-items/reducer'

import {getReservedForFieldValues} from '../../state/reservation/reserved-for-field-values/actions'
import useReservedForFieldValuesReducer from '../../state/reservation/reserved-for-field-values/reducer'

import {getReservedForFields} from '../../state/reservation/reserved-for-fields/actions'
import useReservedForFieldsReducer from '../../state/reservation/reserved-for-fields/reducer'

import {getFilterFieldValues} from '../../state/reservation/filter-field-values/actions'
import usefilterFieldValuesReducer from '../../state/reservation/filter-field-values/reducer'

import {
  createReservation,
  resetReservation,
} from '../../state/reservation/create-reservation/actions'
import useReservationsReducer from '../../state/reservation/create-reservation/reducer'

import {editReservation} from '../../state/reservation/edit-reservation/actions'
import useEditReservationReducer from '../../state/reservation/edit-reservation/reducer'

const ReservationContext = React.createContext()

const ReservationProvider = ({children}) => {
  const [popReserve, setPopReserve] = useState(false)
  const [reserveLimit] = useState(30)
  const [configuration, configurationDispatch] = useGetConfigurationReducer()
  // items
  const [entityObjects, entityObjectsDispatch] = useGetReservationEntityItemsReducer()
  const [checkObjects, checkObjectsDispatch] = useGetReservationEntityItemsReducer()
  // fields
  const [entity, entityDispatch] = useEntityReducer()
  // entities
  const [entities, entitiesDispatch] = useGetStepEntitiesReducer()

  const [reservedForFields, reservedForFieldsDispatch] = useReservedForFieldsReducer()

  const [filterFieldValues, filterFieldValuesDispatch] = usefilterFieldValuesReducer()

  const [
    reservedForFieldValues,
    reservedForFieldValuesDispatch,
  ] = useReservedForFieldValuesReducer()

  const [reservations, reservationsDispatch] = useReservationsReducer()
  const [openReserveForPopOver, setOpenReserveForPopOver] = useState(false)
  // will use snake case for easier json
  const [reservation, setReservation] = useState({
    fromGantt: false,
    isSelection: false,
    is_edit: false,
    date_from: null,
    date_to: null,
    selected_items: [],
    reserve_for_field: null,
    comments: '',
    reservation_notification: [{time: 30, unit: 'minutes'}],
    selected_group: null,
    reserved_for: {},
    action_ids: [],
    selected_field: {},
    reservation_uid: '',
    original_items: [],
    states: '',
    status: '',
  })

  const {selected_group, reserved_for, reserve_for_field, selected_items} = reservation // deconstruct to avoid rerenders

  const update = useCallback(
    (k, v) => {
      setReservation(r => ({...r, [k]: v}))
    },
    [setReservation]
  )

  const clearReservation = useCallback(() => {
    setReservation({
      fromGantt: false,
      isSelection: false,
      is_edit: false,
      date_from: null,
      date_to: null,
      selected_items: [],
      reserve_for_field: null,
      comments: '',
      reservation_notification: [{time: 30, unit: 'minutes'}],
      selected_group: null,
      selected_group_id: null,
      reserved_for: {},
      action_ids: [],
      selected_field: {},
      reservation_uid: '',
      original_items: [],
      states: '',
      status: '',
    })
  }, [setReservation])

  const clearReservationsData = () => {
    resetReservation(reservationsDispatch)
  }

  // get reserved for data
  const loadReservedForFields = useCallback(
    account_id => {
      getReservedForFields(reservedForFieldsDispatch, account_id)
    },
    [reservedForFieldsDispatch]
  )

  // get reserved for field values data
  const loadReservedForFieldValues = useCallback(
    (fieldIds, fieldType, search) => {
      getReservedForFieldValues(reservedForFieldValuesDispatch, fieldIds, fieldType, search)
    },
    [reservedForFieldValuesDispatch]
  )

  // get reserved for data
  const loadFilterFieldValues = useCallback(
    (field_id, search, params) => {
      getFilterFieldValues(filterFieldValuesDispatch, field_id, search, params)
    },
    [filterFieldValuesDispatch]
  )

  // get configuration data
  const loadConfiguration = useCallback(
    group_id => {
      getConfiguration(configurationDispatch, {group_id})
      getEntity(group_id, entityDispatch)
    },
    [configurationDispatch, entityDispatch]
  )

  const loadEntityObjects = useCallback(
    (group_id, params = {}) => {
      getReservationEntityItems(entityObjectsDispatch, group_id, params)
    },
    [entityObjectsDispatch]
  )

  const checkEntityObjects = useCallback(
    (group_id, params = {}) => {
      getReservationEntityItems(checkObjectsDispatch, group_id, params)
    },
    [checkObjectsDispatch]
  )
  const loadEntities = useCallback(
    ({field_ids, field_type, object_id}) => {
      getStepEntities(entitiesDispatch, {field_ids, field_type, object_id})
    },
    [entitiesDispatch]
  )

  // get configuration data
  const addReservation = useCallback(
    reservationData => {
      createReservation(reservationsDispatch, reservationData)
    },
    [reservationsDispatch]
  )

  const editReservedReservation = useCallback(
    reservationData => {
      editReservation(reservationsDispatch, reservationData)
    },
    [reservationsDispatch]
  )
  // load entities/group list
  useEffect(() => {
    if (!_.isEmpty(reserved_for) && !_.isEmpty(reserve_for_field)) {
      loadEntities({
        field_ids: reserve_for_field.field_details.map(f => f.field_id).join(','),
        field_type: reserve_for_field.field_type,
        object_id: reserved_for.id,
      })
    }
  }, [loadEntities, reserved_for, reserve_for_field])

  const entityFields = useMemo(() => {
    const field_len = entity?.entities?.entities_details?.length
    if (!!field_len) {
      return entity.entities?.entities_details[0].fields
    }
    return []
  }, [entity])

  const checkSelectedItems = useMemo(() => {
    let items = []
    let finalValues = []
    let abc = []
    if (checkObjects.data.entity_objects) {
      checkObjects.data.entity_objects.forEach((o, i) => {
        let store = false
        selected_items.forEach(a => {
          if (o.id === a.id) {
            store = true
          }
        })
        if (store) {
          items.push(o)
        }
      })
    }
    selected_items.forEach(a => {
      if (items.id !== a.id && items.indexOf(a) === -1) {
        finalValues = items
      }
    })
    return finalValues
  }, [checkObjects, selected_items])

  const formatSelectedItems = useMemo(() => {
    if (entityFields.length > 0) {
      const primary_field = entityFields.find(f => f.is_primary_default)
      const secondary_field = entityFields.find(f => f.is_secondary_default)

      return selected_items.map(e => ({
        id: e.id,
        label: e.name
          ? e.name
          : `${!!primary_field && e[primary_field?.key] ? `${e[primary_field?.key]}` : ' - '}${
              !!secondary_field && e[secondary_field?.key] ? ` | ${e[secondary_field.key]}` : ' | -'
            }`,
      }))
    } else {
      return selected_items.map(e => ({
        id: e.id,
        label: e.name,
      }))
    }
  }, [selected_items, entityFields])

  const value = {
    popReserve,
    setPopReserve,
    reservation,
    setReservation,
    reservations,
    formatSelectedItems,
    reservedForFields,
    filterFieldValues,
    loadReservedForFields,
    reservedForFieldValues,
    loadFilterFieldValues,
    loadReservedForFieldValues,
    addReservation,
    editReservedReservation,
    update,
    loadConfiguration,
    configuration,
    loadEntityObjects,
    checkEntityObjects,
    checkObjects,
    entityObjects: entityObjects,
    entityFields: entityFields,
    loadEntities,
    entities,
    clearReservation,
    openReserveForPopOver,
    setOpenReserveForPopOver,
    clearReservationsData,
    reserveLimit,
  }

  return <ReservationContext.Provider value={value}>{children}</ReservationContext.Provider>
}

const ReservationConsumer = ReservationContext.Consumer

export {ReservationProvider, ReservationContext, ReservationConsumer}
